import Icon, {
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import type {
  AgentActivityResolvedRisk,
  AgentActivityRiskExplanation,
  AgentActivityRiskLevel,
  AgentActivityRiskPermission,
} from "@p0security/integrations-agentic-shared/interface";
import { assertNever } from "@p0security/shared/types/index";
import { Flex, Row, Timeline, Tooltip, Typography } from "@p0security/ui";
import classNames from "classnames";
import { compact } from "lodash-es";
import pluralize from "pluralize";

const RISK_LEVEL_BG_CLASS: Record<AgentActivityRiskLevel, string> = {
  Critical: "bg-(--ant-color-error-active)",
  High: "bg-(--ant-color-error)",
  Medium: "bg-(--ant-color-warning)",
  Low: "bg-(--ant-color-info)",
  Unknown: "bg-(--ant-color-text-description)",
};

// antd's own light alert-style tint for each severity family — Critical and
// High share "error" (antd has no separate, more-severe error tint), and
// Unknown has no semantic tint of its own, so it falls back to a neutral fill.
const RISK_LEVEL_TINT_CLASS: Record<AgentActivityRiskLevel, string> = {
  Critical: "bg-(--ant-color-error-bg)",
  High: "bg-(--ant-color-error-bg)",
  Medium: "bg-(--ant-color-warning-bg)",
  Low: "bg-(--ant-color-info-bg)",
  Unknown: "bg-(--ant-color-fill-tertiary)",
};

const RISK_LEVEL_ICON: Record<AgentActivityRiskLevel, typeof Icon> = {
  Critical: CloseCircleOutlined,
  High: WarningOutlined,
  Medium: ExclamationCircleOutlined,
  Low: InfoCircleOutlined,
  Unknown: QuestionCircleOutlined,
};

// The badge's box and its icon, per size.
const RISK_BADGE_SIZE_CLASSES = {
  small: { badge: "w-5 h-5 rounded-sm", icon: "[&>svg]:w-2.5 [&>svg]:h-2.5" },
  default: { badge: "w-8.5 h-8.5 rounded-lg", icon: "[&>svg]:w-4 [&>svg]:h-4" },
  large: {
    badge: "w-9.5 h-9.5 rounded-lg",
    icon: "[&>svg]:w-4.75 [&>svg]:h-4.75",
  },
} as const;

type RiskBadgeSize = keyof typeof RISK_BADGE_SIZE_CLASSES;

/** The circular, level-colored icon this file uses everywhere it names a
 * risk level: the Risk tag itself, and each risk group in its tooltip. */
export const RiskLevelBadge: React.FC<{
  className?: string;
  level: AgentActivityRiskLevel;
  size?: RiskBadgeSize;
}> = ({ className, level, size = "default" }) => {
  const RiskIcon = RISK_LEVEL_ICON[level];
  return (
    <div
      className={classNames(
        RISK_LEVEL_BG_CLASS[level],
        RISK_BADGE_SIZE_CLASSES[size].badge,
        "flex items-center justify-center",
        className
      )}
    >
      <RiskIcon
        className={classNames(
          RISK_BADGE_SIZE_CLASSES[size].icon,
          "text-(--ant-color-bg-base)!"
        )}
      />
    </div>
  );
};

type AnnotationRiskExplanation = Extract<
  AgentActivityRiskExplanation,
  { source: "annotation" }
>;

/** Which hint(s) decided an annotation-derived level, in the MCP-spec order
 * `resolveToolRisk` itself branches on (readOnlyHint alone, else
 * destructiveHint defaulting true, else also idempotentHint defaulting
 * false) — see `packages/integrations/agentic/backend/src/activity/risk.ts`.
 * Display only: the level itself is already decided by the time this runs. */
const decisiveHints = (
  explanation: AnnotationRiskExplanation
): { hint: string; value: boolean }[] => {
  const { destructiveHint, idempotentHint, readOnlyHint } = explanation;
  if (readOnlyHint === true) return [{ hint: "readOnlyHint", value: true }];
  const destructive = destructiveHint ?? true;
  if (!destructive) return [{ hint: "destructiveHint", value: false }];
  return [
    { hint: "destructiveHint", value: true },
    { hint: "idempotentHint", value: idempotentHint ?? false },
  ];
};

const AnnotationExplanation: React.FC<{
  explanation: AnnotationRiskExplanation;
}> = ({ explanation }) => (
  <Flex gap={4} vertical>
    <Typography.Text>
      Resolved from tool annotations:{" "}
      {decisiveHints(explanation)
        .map(({ hint, value }) => `${hint}=${value}`)
        .join(", ")}
    </Typography.Text>
    {explanation.pending && (
      <Typography.Text>
        {"IAM-based risk wasn't available for this read."}
      </Typography.Text>
    )}
  </Flex>
);

// Every permission in a group shares the group's riskId, so they also
// share its name and score — only `permissions` itself is plural.
type PermissionGroup = {
  permissions: string[];
  riskName: string;
  score: AgentActivityRiskLevel;
};

const groupByRisk = (
  permissions: AgentActivityRiskPermission[]
): PermissionGroup[] => {
  const groups = new Map<string, PermissionGroup>();
  for (const { permission, riskId, riskName, score } of permissions) {
    const group = groups.get(riskId);
    if (group) group.permissions.push(permission);
    else groups.set(riskId, { permissions: [permission], riskName, score });
  }
  return [...groups.values()];
};

const PermissionGroupRow: React.FC<{ group: PermissionGroup }> = ({
  group: { permissions, riskName },
}) => (
  <Flex vertical>
    <Typography.Text strong>{riskName}</Typography.Text>
    <Typography.Text className="font-mono text-xs" type="secondary">
      {permissions.join(", ")}
    </Typography.Text>
  </Flex>
);

const IamExplanation: React.FC<{
  explanation: Extract<AgentActivityRiskExplanation, { source: "iam" }>;
  level: AgentActivityRiskLevel;
}> = ({ explanation: { permissions, unrecognizedPermissions }, level }) => {
  const groups = groupByRisk(permissions);
  const unrecognizedCount = unrecognizedPermissions?.length ?? 0;
  const totalCount = permissions.length + unrecognizedCount;

  if (totalCount === 0) {
    return (
      <Typography.Text>No mapped IAM permission carries risk.</Typography.Text>
    );
  }

  const signalCount = groups.length + (unrecognizedCount > 0 ? 1 : 0);
  const items = compact([
    ...groups.map((group) => ({
      children: <PermissionGroupRow group={group} />,
      dot: <RiskLevelBadge level={group.score} size="small" />,
    })),
    unrecognizedCount > 0
      ? {
          children: (
            <PermissionGroupRow
              group={{
                permissions: unrecognizedPermissions ?? [],
                riskName: "Uncategorized",
                score: "Unknown" as const,
              }}
            />
          ),
          dot: <RiskLevelBadge level="Unknown" size="small" />,
        }
      : null,
  ]);

  return (
    <div className="w-72">
      <Row
        className={classNames("rounded-md p-3 ", RISK_LEVEL_TINT_CLASS[level])}
      >
        <Typography.Text className="block text-2xl leading-tight mr-1" strong>
          {totalCount}
        </Typography.Text>
        <Typography.Text strong className="mr-1">
          {pluralize("Total Permission", totalCount)}
        </Typography.Text>
        <Typography.Text className="block text-xs" type="secondary">
          (Across {signalCount} {pluralize("signal", signalCount)})
        </Typography.Text>
      </Row>
      <Row className="pt-3 pl-3">
        <Timeline
          className="[&_li.ant-timeline-item-last]:pb-0!"
          items={items}
        />
      </Row>
    </div>
  );
};

const RiskExplanation: React.FC<AgentActivityResolvedRisk> = ({
  explanation,
  level,
}) => {
  switch (explanation.source) {
    case "annotation":
      return <AnnotationExplanation explanation={explanation} />;
    case "iam":
      return <IamExplanation explanation={explanation} level={level} />;
    default:
      return assertNever(explanation);
  }
};

export const RiskTag: React.FC<{
  resolvedRisk?: AgentActivityResolvedRisk;
  /** Suppresses the explanation tooltip, for a usage (such as the risk
   * filter's own option list) that renders a level with no real resolved
   * explanation behind it. */
  hideTooltip?: boolean;
  size?: RiskBadgeSize;
}> = ({ hideTooltip = false, resolvedRisk, size = "default" }) => {
  if (!resolvedRisk)
    return <Typography.Text type="secondary">—</Typography.Text>;
  const { level } = resolvedRisk;
  const tag = (
    <Row wrap={false} align="middle">
      <RiskLevelBadge className="mr-2" level={level} size={size} />
      <Typography.Text strong={size !== "small"}>{level}</Typography.Text>
    </Row>
  );
  if (hideTooltip) return tag;
  return (
    <Tooltip
      classNames={{ body: "text-(--ant-color-text)!", root: "max-w-[360px]!" }}
      color="var(--ant-color-bg-elevated)"
      title={<RiskExplanation {...resolvedRisk} />}
      trigger={["hover", "focus"]}
    >
      <span className="inline-block" tabIndex={0}>
        {tag}
      </span>
    </Tooltip>
  );
};
